import { Field, TextArea } from "../crm/Form";
import PartsTable from "./PartsTable";
import TestReadingsTable from "./TestReadingsTable";

/**
 * Editor sections — controlled inputs. The page owner debounces saves
 * via PATCH on blur to avoid a deluge of writes per keystroke.
 */
export default function DiagnosticReportEditor({
  draft,
  onChange,
  onCommitField,
  readOnly,
}) {
  const set = (patch) => onChange({ ...draft, ...patch });

  return (
    <div className="space-y-8" data-testid="diagnostic-editor">
      <section>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Findings
        </h3>
        <p className="text-xs text-slate-500 mb-2">
          Internal — what was observed on site.
        </p>
        <Field label="Findings">
          <TextArea
            value={draft.findings_text || ""}
            onChange={(v) => set({ findings_text: v })}
            onBlur={() => onCommitField("findings_text")}
            rows={5}
            disabled={readOnly}
            data-testid="findings-textarea"
          />
        </Field>
      </section>

      <section>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Recommendations
        </h3>
        <p className="text-xs text-slate-500 mb-2">
          Internal — proposed next steps.
        </p>
        <Field label="Recommendations">
          <TextArea
            value={draft.recommendations_text || ""}
            onChange={(v) => set({ recommendations_text: v })}
            onBlur={() => onCommitField("recommendations_text")}
            rows={4}
            disabled={readOnly}
            data-testid="recommendations-textarea"
          />
        </Field>
      </section>

      <section>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Parts Required
        </h3>
        <p className="text-xs text-slate-500 mb-2">Free-text parts list.</p>
        <PartsTable
          value={draft.parts_required || []}
          onChange={(rows) => {
            set({ parts_required: rows });
            // Only commit rows whose label is non-empty — server validates min_length.
            const valid = rows.filter((r) => (r.label || "").trim().length > 0);
            onCommitField("parts_required", valid);
          }}
          disabled={readOnly}
        />
      </section>

      <section>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Test Readings
        </h3>
        <p className="text-xs text-slate-500 mb-2">
          Voltages, currents, production figures.
        </p>
        <TestReadingsTable
          value={draft.test_readings || []}
          onChange={(rows) => {
            set({ test_readings: rows });
            const valid = rows.filter(
              (r) =>
                (r.label || "").trim().length > 0 &&
                (r.value || "").trim().length > 0
            );
            onCommitField("test_readings", valid);
          }}
          disabled={readOnly}
        />
      </section>

      <section>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Owner Summary
        </h3>
        <p className="text-xs text-slate-500 mb-2">
          Customer-safe wording — used for owner-facing views.
        </p>
        <Field label="Owner Summary">
          <TextArea
            value={draft.owner_summary || ""}
            onChange={(v) => set({ owner_summary: v })}
            onBlur={() => onCommitField("owner_summary")}
            rows={5}
            disabled={readOnly}
            data-testid="owner-summary-textarea"
          />
        </Field>
      </section>
    </div>
  );
}
